import type { ChangeEvent } from 'react';

import useFormContext from '@src/shared/form/useFormContext';
import Avatar from '@src/shared/ui/Avatar';
import { Button } from '@src/shared/ui/Button';
import { AccountBoxIcon, DeleteIcon, UploadIcon } from '@src/shared/ui/icons';
import { Box } from '@src/shared/ui/layout';

const StyleSheet = {
  root: {
    mb: 2.5,
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    backgroundColor: 'grey.200',
    borderRadius: 1,
    mr: 1.25,
    width: 40,
    height: 40,
  },
};

type FormContextShape = {
  setValue: (
    name: string,
    value: unknown,
    options?: { shouldDirty?: boolean; shouldValidate?: boolean },
  ) => void;
  watch: (name: string) => unknown;
};

type ImageUploadFieldProps = {
  name: string;
  label: string;
  disabled?: boolean;
  styles?: Record<string, any>;
  EmptyFiller?: React.ReactNode;
};

function ImageUploadField({ name, label, disabled = false, styles = {}, EmptyFiller}: ImageUploadFieldProps) {
  const { setValue, watch } = useFormContext() as FormContextShape;
  const previewName = `${name}Preview`;
  const imagePreview = watch(previewName) as string | null;

  const clearImage = () => {
    setValue(name, null, { shouldDirty: true, shouldValidate: true });
    setValue(previewName, null, { shouldDirty: true, shouldValidate: true });
  };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    setValue(name, file, { shouldDirty: true, shouldValidate: true });

    if (!file) {
      setValue(previewName, null, { shouldDirty: true, shouldValidate: true });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setValue(previewName, typeof reader.result === 'string' ? reader.result : null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    };
    reader.readAsDataURL(file);
  };

  return (
    <Box sx={styles.root || StyleSheet.root}>
      <Avatar src={imagePreview ?? undefined} sx={styles.image || StyleSheet.avatar}>
        {EmptyFiller ?? <AccountBoxIcon />}
      </Avatar>

      {imagePreview ? (
        <Button
          variant="outlined"
          onClick={clearImage}
          disabled={disabled}
          startIcon={<DeleteIcon color="inherit" />}
        >
          Delete {label}
        </Button>
      ) : (
        <Button
          component="label"
          variant="outlined"
          disabled={disabled}
          startIcon={<UploadIcon color="inherit" />}
        >
          Upload {label}
          <input hidden type="file" accept="image/*" onChange={handleImageChange} />
        </Button>
      )}
    </Box>
  );
}

export default ImageUploadField;
